// apps/dashboard/src/stores/chatStore.ts
import { create } from 'zustand';
import type { ArtifactEvent, ArtifactWithMeta } from '@freelancer-os/shared-types';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  streaming?: boolean;
}

interface ChatState {
  messages: ChatMessage[];
  status: string | null;
  isStreaming: boolean;
  artifacts: ArtifactWithMeta[];
  error: string | null;
  pendingHydration: ArtifactEvent['data'] | null;
  threadId: string | null;
  showNewChatPlaceholder: boolean;

  addUserMessage: (content: string) => void;
  startAssistantMessage: () => string;
  appendToken: (id: string, content: string) => void;
  finalizeAssistantMessage: (id: string) => void;
  setStatus: (status: string | null) => void;
  appendArtifact: (data: ArtifactEvent['data']) => void;
  setIsStreaming: (streaming: boolean) => void;
  setError: (error: string | null) => void;
  setPendingHydration: (data: ArtifactEvent['data'] | null) => void;
  setThreadId: (threadId: string | null) => void;
  loadThread: (threadId: string, messages: ChatMessage[], artifacts?: ArtifactWithMeta[]) => void;
  reset: () => void;
}

function getTimestamp() {
  const now = new Date();
  return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
}

export const useChatStore = create<ChatState>((set) => ({
  messages: [
    {
      id: 'system-1',
      role: 'system',
      content: 'FreelancerOS AI — sẵn sàng tạo hoá đơn từ tin nhắn của bạn',
      timestamp: '',
    },
  ],
  status: null,
  isStreaming: false,
  artifacts: [],
  error: null,
  pendingHydration: null,
  threadId: null,
  showNewChatPlaceholder: false,

  addUserMessage: (content) =>
    set((s) => ({
      messages: [
        ...s.messages,
        { id: `user-${Date.now()}`, role: 'user', content, timestamp: getTimestamp() },
      ],
    })),

  startAssistantMessage: () => {
    const id = `ai-${Date.now()}`;
    set((s) => ({
      messages: [
        ...s.messages,
        { id, role: 'assistant', content: '', timestamp: getTimestamp(), streaming: true },
      ],
    }));
    return id;
  },

  appendToken: (id, content) =>
    set((s) => ({
      messages: s.messages.map((m) => (m.id === id ? { ...m, content: m.content + content } : m)),
    })),

  finalizeAssistantMessage: (id) =>
    set((s) => ({
      messages: s.messages.map((m) => (m.id === id ? { ...m, streaming: false } : m)),
    })),

  setStatus: (status) => set({ status }),
  appendArtifact: (data) =>
    set((s) => ({
      artifacts: [...s.artifacts, { artifactIndex: s.artifacts.length, data }],
    })),
  setIsStreaming: (isStreaming) => set({ isStreaming }),
  setError: (error) => set({ error }),
  setPendingHydration: (pendingHydration) => set({ pendingHydration }),
  setThreadId: (threadId) => set({ threadId, showNewChatPlaceholder: false }),

  loadThread: (threadId, messages, artifacts = []) =>
    set({
      threadId,
      messages: [
        {
          id: 'system-1',
          role: 'system',
          content: 'FreelancerOS AI — sẵn sàng tạo hoá đơn từ tin nhắn của bạn',
          timestamp: '',
        },
        ...messages,
      ],
      artifacts,
      status: null,
      isStreaming: false,
      error: null,
      pendingHydration: null,
      showNewChatPlaceholder: false,
    }),

  reset: () =>
    set({
      messages: [
        {
          id: 'system-1',
          role: 'system',
          content: 'FreelancerOS AI — sẵn sàng tạo hoá đơn từ tin nhắn của bạn',
          timestamp: '',
        },
      ],
      status: null,
      isStreaming: false,
      artifacts: [],
      error: null,
      pendingHydration: null,
      threadId: null,
      showNewChatPlaceholder: true,
    }),
}));
